import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import autopilotCampaignService from '../../services/autopilotCampaign';
import { getJobs } from '../../services/api';

function CampaignForm({ campaign = null, onSave, onCancel }) {
    const [jobs, setJobs] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [activeStep, setActiveStep] = useState(0);

    const [formData, setFormData] = useState({
        name: campaign?.name || '',
        targetJob: campaign?.targetJob?._id || '',
        targetCriteria: {
            minMatchScore: campaign?.targetCriteria?.minMatchScore || 75,
            skills: campaign?.targetCriteria?.skills || [],
            experienceLevel: campaign?.targetCriteria?.experienceLevel || [],
            locations: campaign?.targetCriteria?.locations || [],
            excludeContacted: campaign?.targetCriteria?.excludeContacted ?? true
        },
        emailSequence: {
            initialEmail: {
                template: campaign?.emailSequence?.initialEmail?.template || 'professional',
                tone: campaign?.emailSequence?.initialEmail?.tone || 'friendly',
                customInstructions: campaign?.emailSequence?.initialEmail?.customInstructions || ''
            },
            followUps: campaign?.emailSequence?.followUps || [
                { delayDays: 3, template: 'follow_up_1' },
                { delayDays: 5, template: 'follow_up_2' }
            ],
            maxFollowUps: campaign?.emailSequence?.maxFollowUps || 3
        },
        automation: {
            autoApprove: campaign?.automation?.autoApprove ?? false,
            autoApproveThreshold: campaign?.automation?.autoApproveThreshold || 90,
            autoSendEmails: campaign?.automation?.autoSendEmails ?? false,
            dailyLimit: campaign?.automation?.dailyLimit || 50
        },
        scheduling: {
            enabled: campaign?.scheduling?.enabled ?? false,
            provider: campaign?.scheduling?.provider || 'google_calendar',
            interviewDuration: campaign?.scheduling?.interviewDuration || 30
        }
    });

    const [newSkill, setNewSkill] = useState('');
    const [newLocation, setNewLocation] = useState('');

    useEffect(() => {
        fetchJobs();
    }, []);

    const fetchJobs = async () => {
        try {
            const response = await getJobs();
            setJobs(response.data.jobs || []);
        } catch (error) {
            console.error('Error fetching jobs:', error);
        }
    };

    const handleChange = (path, value) => {
        setFormData(prev => {
            const newData = { ...prev };
            const keys = path.split('.');
            let current = newData;

            for (let i = 0; i < keys.length - 1; i++) {
                current = current[keys[i]];
            }
            current[keys[keys.length - 1]] = value;

            return newData;
        });
    };

    const addSkill = () => {
        if (newSkill.trim()) {
            handleChange('targetCriteria.skills', [...formData.targetCriteria.skills, newSkill.trim()]);
            setNewSkill('');
        }
    };

    const removeSkill = (skill) => {
        handleChange('targetCriteria.skills', formData.targetCriteria.skills.filter(s => s !== skill));
    };

    const addLocation = () => {
        if (newLocation.trim()) {
            handleChange('targetCriteria.locations', [...formData.targetCriteria.locations, newLocation.trim()]);
            setNewLocation('');
        }
    };

    const removeLocation = (location) => {
        handleChange('targetCriteria.locations', formData.targetCriteria.locations.filter(l => l !== location));
    };

    const handleSubmit = async () => {
        if (!formData.name.trim()) {
            toast.error('Campaign name is required');
            return;
        }
        if (!formData.targetJob) {
            toast.error('Please select a target job');
            return;
        }

        setIsLoading(true);
        try {
            if (campaign) {
                await autopilotCampaignService.updateCampaign(campaign._id, formData);
                toast.success('Campaign updated!');
            } else {
                await autopilotCampaignService.createCampaign(formData);
                toast.success('Campaign created!');
            }
            onSave();
        } catch (error) {
            console.error('Error saving campaign:', error);
            toast.error('Failed to save campaign');
        } finally {
            setIsLoading(false);
        }
    };

    const steps = [
        { title: 'Basic Info', icon: '📋' },
        { title: 'Targeting', icon: '🎯' },
        { title: 'Email Sequence', icon: '📧' },
        { title: 'Automation', icon: '⚡' }
    ];

    return (
        <div className="bg-white rounded-lg shadow-lg max-w-4xl mx-auto">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                    <h2 className="text-xl font-semibold text-gray-900">
                        {campaign ? 'Edit Campaign' : 'Create New Campaign'}
                    </h2>
                    <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Step Indicators */}
                <div className="flex items-center gap-4 mt-4">
                    {steps.map((step, idx) => (
                        <button
                            key={idx}
                            onClick={() => setActiveStep(idx)}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                                activeStep === idx
                                    ? 'bg-indigo-100 text-indigo-700'
                                    : 'text-gray-500 hover:bg-gray-100'
                            }`}
                        >
                            <span>{step.icon}</span>
                            <span className="font-medium text-sm">{step.title}</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* Form Content */}
            <div className="p-6 min-h-[400px]">
                {/* Step 0: Basic Info */}
                {activeStep === 0 && (
                    <div className="space-y-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Campaign Name *
                            </label>
                            <input
                                type="text"
                                value={formData.name}
                                onChange={(e) => handleChange('name', e.target.value)}
                                placeholder="e.g., Senior Engineers Q1 2024"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Target Job *
                            </label>
                            <select
                                value={formData.targetJob}
                                onChange={(e) => handleChange('targetJob', e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            >
                                <option value="">Select a job...</option>
                                {jobs.map(job => (
                                    <option key={job._id} value={job._id}>
                                        {job.title} - {job.company || 'Company'}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                )}

                {/* Step 1: Targeting */}
                {activeStep === 1 && (
                    <div className="space-y-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Minimum Match Score: {formData.targetCriteria.minMatchScore}%
                            </label>
                            <input
                                type="range"
                                min="50"
                                max="95"
                                step="5"
                                value={formData.targetCriteria.minMatchScore}
                                onChange={(e) => handleChange('targetCriteria.minMatchScore', parseInt(e.target.value))}
                                className="w-full"
                            />
                            <div className="flex justify-between text-xs text-gray-500 mt-1">
                                <span>50% (More candidates)</span>
                                <span>95% (Highly qualified)</span>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Required Skills
                            </label>
                            <div className="flex gap-2 mb-2">
                                <input
                                    type="text"
                                    value={newSkill}
                                    onChange={(e) => setNewSkill(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && addSkill()}
                                    placeholder="Add a skill..."
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                />
                                <button
                                    onClick={addSkill}
                                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700"
                                >
                                    Add
                                </button>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {formData.targetCriteria.skills.map(skill => (
                                    <span key={skill} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm flex items-center gap-1">
                                        {skill}
                                        <button onClick={() => removeSkill(skill)} className="ml-1 text-blue-500 hover:text-blue-700">×</button>
                                    </span>
                                ))}
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Experience Level
                            </label>
                            <div className="flex flex-wrap gap-2">
                                {['junior', 'mid', 'senior', 'lead', 'executive'].map(level => (
                                    <button
                                        key={level}
                                        onClick={() => {
                                            const current = formData.targetCriteria.experienceLevel;
                                            const newLevels = current.includes(level)
                                                ? current.filter(l => l !== level)
                                                : [...current, level];
                                            handleChange('targetCriteria.experienceLevel', newLevels);
                                        }}
                                        className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                                            formData.targetCriteria.experienceLevel.includes(level)
                                                ? 'bg-indigo-600 text-white'
                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                        }`}
                                    >
                                        {level.charAt(0).toUpperCase() + level.slice(1)}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Preferred Locations
                            </label>
                            <div className="flex gap-2 mb-2">
                                <input
                                    type="text"
                                    value={newLocation}
                                    onChange={(e) => setNewLocation(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && addLocation()}
                                    placeholder="Add a location..."
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                />
                                <button
                                    onClick={addLocation}
                                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700"
                                >
                                    Add
                                </button>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {formData.targetCriteria.locations.map(location => (
                                    <span key={location} className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm flex items-center gap-1">
                                        {location}
                                        <button onClick={() => removeLocation(location)} className="ml-1 text-green-500 hover:text-green-700">×</button>
                                    </span>
                                ))}
                            </div>
                        </div>

                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                id="excludeContacted"
                                checked={formData.targetCriteria.excludeContacted}
                                onChange={(e) => handleChange('targetCriteria.excludeContacted', e.target.checked)}
                                className="h-4 w-4 rounded border-gray-300 text-indigo-600"
                            />
                            <label htmlFor="excludeContacted" className="ml-2 text-sm text-gray-700">
                                Exclude previously contacted candidates
                            </label>
                        </div>
                    </div>
                )}

                {/* Step 2: Email Sequence */}
                {activeStep === 2 && (
                    <div className="space-y-6">
                        <div className="bg-blue-50 p-4 rounded-lg">
                            <h3 className="font-medium text-blue-900 mb-2">Initial Email</h3>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Template Style</label>
                                    <select
                                        value={formData.emailSequence.initialEmail.template}
                                        onChange={(e) => handleChange('emailSequence.initialEmail.template', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                    >
                                        <option value="professional">Professional</option>
                                        <option value="casual">Casual</option>
                                        <option value="enthusiastic">Enthusiastic</option>
                                        <option value="direct">Direct & Concise</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Tone</label>
                                    <select
                                        value={formData.emailSequence.initialEmail.tone}
                                        onChange={(e) => handleChange('emailSequence.initialEmail.tone', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                    >
                                        <option value="friendly">Friendly</option>
                                        <option value="formal">Formal</option>
                                        <option value="enthusiastic">Enthusiastic</option>
                                        <option value="personable">Personable</option>
                                    </select>
                                </div>
                            </div>
                            <div className="mt-3">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Custom Instructions (Optional)</label>
                                <textarea
                                    value={formData.emailSequence.initialEmail.customInstructions}
                                    onChange={(e) => handleChange('emailSequence.initialEmail.customInstructions', e.target.value)}
                                    placeholder="E.g., Mention our recent Series B funding, emphasize remote-first culture..."
                                    rows={2}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                />
                            </div>
                        </div>

                        <div>
                            <h3 className="font-medium text-gray-900 mb-3">Follow-up Sequence</h3>
                            <div className="space-y-3">
                                {formData.emailSequence.followUps.map((followUp, idx) => (
                                    <div key={idx} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                                        <span className="text-sm font-medium text-gray-500">Follow-up {idx + 1}</span>
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm text-gray-600">Send after</span>
                                            <input
                                                type="number"
                                                min="1"
                                                max="30"
                                                value={followUp.delayDays}
                                                onChange={(e) => {
                                                    const newFollowUps = [...formData.emailSequence.followUps];
                                                    newFollowUps[idx].delayDays = parseInt(e.target.value);
                                                    handleChange('emailSequence.followUps', newFollowUps);
                                                }}
                                                className="w-16 px-2 py-1 border border-gray-300 rounded text-sm text-center"
                                            />
                                            <span className="text-sm text-gray-600">days</span>
                                        </div>
                                        <button
                                            onClick={() => {
                                                const newFollowUps = formData.emailSequence.followUps.filter((_, i) => i !== idx);
                                                handleChange('emailSequence.followUps', newFollowUps);
                                            }}
                                            className="ml-auto text-red-500 hover:text-red-700"
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                        </button>
                                    </div>
                                ))}
                            </div>
                            {formData.emailSequence.followUps.length < 5 && (
                                <button
                                    onClick={() => {
                                        const lastDelay = formData.emailSequence.followUps.length > 0
                                            ? formData.emailSequence.followUps[formData.emailSequence.followUps.length - 1].delayDays + 3
                                            : 3;
                                        handleChange('emailSequence.followUps', [
                                            ...formData.emailSequence.followUps,
                                            { delayDays: lastDelay, template: `follow_up_${formData.emailSequence.followUps.length + 1}` }
                                        ]);
                                    }}
                                    className="mt-3 text-sm text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                    </svg>
                                    Add Follow-up
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {/* Step 3: Automation */}
                {activeStep === 3 && (
                    <div className="space-y-6">
                        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-lg">
                            <h3 className="font-medium text-gray-900 mb-4">Automation Settings</h3>

                            <div className="space-y-4">
                                <div className="flex items-start gap-3">
                                    <input
                                        type="checkbox"
                                        id="autoApprove"
                                        checked={formData.automation.autoApprove}
                                        onChange={(e) => handleChange('automation.autoApprove', e.target.checked)}
                                        className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600"
                                    />
                                    <div>
                                        <label htmlFor="autoApprove" className="font-medium text-gray-900">
                                            Auto-approve high-scoring candidates
                                        </label>
                                        <p className="text-sm text-gray-500">
                                            Automatically approve candidates above threshold without manual review
                                        </p>
                                        {formData.automation.autoApprove && (
                                            <div className="mt-2 flex items-center gap-2">
                                                <span className="text-sm text-gray-600">Threshold:</span>
                                                <input
                                                    type="number"
                                                    min="80"
                                                    max="100"
                                                    value={formData.automation.autoApproveThreshold}
                                                    onChange={(e) => handleChange('automation.autoApproveThreshold', parseInt(e.target.value))}
                                                    className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                                                />
                                                <span className="text-sm text-gray-600">% match score</span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="flex items-start gap-3">
                                    <input
                                        type="checkbox"
                                        id="autoSendEmails"
                                        checked={formData.automation.autoSendEmails}
                                        onChange={(e) => handleChange('automation.autoSendEmails', e.target.checked)}
                                        className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600"
                                    />
                                    <div>
                                        <label htmlFor="autoSendEmails" className="font-medium text-gray-900">
                                            Auto-send emails to approved candidates
                                        </label>
                                        <p className="text-sm text-gray-500">
                                            Automatically send initial outreach when candidates are approved
                                        </p>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Daily Email Limit
                                    </label>
                                    <input
                                        type="number"
                                        min="10"
                                        max="200"
                                        value={formData.automation.dailyLimit}
                                        onChange={(e) => handleChange('automation.dailyLimit', parseInt(e.target.value))}
                                        className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        Maximum emails to send per day (to avoid spam filters)
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-green-50 p-4 rounded-lg">
                            <h3 className="font-medium text-gray-900 mb-4">Interview Scheduling</h3>

                            <div className="flex items-start gap-3 mb-4">
                                <input
                                    type="checkbox"
                                    id="schedulingEnabled"
                                    checked={formData.scheduling.enabled}
                                    onChange={(e) => handleChange('scheduling.enabled', e.target.checked)}
                                    className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600"
                                />
                                <div>
                                    <label htmlFor="schedulingEnabled" className="font-medium text-gray-900">
                                        Enable auto-scheduling
                                    </label>
                                    <p className="text-sm text-gray-500">
                                        Automatically schedule interviews when candidates respond positively
                                    </p>
                                </div>
                            </div>

                            {formData.scheduling.enabled && (
                                <div className="grid grid-cols-2 gap-4 mt-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Provider</label>
                                        <select
                                            value={formData.scheduling.provider}
                                            onChange={(e) => handleChange('scheduling.provider', e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                        >
                                            <option value="google_calendar">Google Calendar</option>
                                            <option value="calendly">Calendly</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Interview Duration</label>
                                        <select
                                            value={formData.scheduling.interviewDuration}
                                            onChange={(e) => handleChange('scheduling.interviewDuration', parseInt(e.target.value))}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                        >
                                            <option value={15}>15 minutes</option>
                                            <option value={30}>30 minutes</option>
                                            <option value={45}>45 minutes</option>
                                            <option value={60}>60 minutes</option>
                                        </select>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <div className="flex gap-2">
                    {activeStep > 0 && (
                        <button
                            onClick={() => setActiveStep(activeStep - 1)}
                            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50"
                        >
                            Previous
                        </button>
                    )}
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={onCancel}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                    {activeStep < steps.length - 1 ? (
                        <button
                            onClick={() => setActiveStep(activeStep + 1)}
                            className="px-6 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700"
                        >
                            Next
                        </button>
                    ) : (
                        <button
                            onClick={handleSubmit}
                            disabled={isLoading}
                            className="px-6 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                        >
                            {isLoading ? (
                                <>
                                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Saving...
                                </>
                            ) : (
                                <>
                                    {campaign ? 'Update Campaign' : 'Create Campaign'}
                                </>
                            )}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

export default CampaignForm;
